// src/pages/health/HealthTimelinePage.tsx
// Health timeline view page with filtering and navigation

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Clock,
  ArrowLeft,
  Filter,
  Calendar,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
} from 'lucide-react';
import { HealthTimeline } from '@/features/health/components/timeline/HealthTimeline';
import { TimelineFilters } from '@/features/health/components/timeline/TimelineFilters';
import type { TimelineFilter } from '@/features/health/types/healthTypes/reportTypes';
import { format, subMonths, startOfMonth, endOfMonth, addMonths } from 'date-fns';

type ViewMode = 'timeline' | 'calendar';

export const HealthTimelinePage: React.FC = () => {
  const { familyId, childId } = useParams<{ familyId: string; childId: string }>();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [showFilters, setShowFilters] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filters, setFilters] = useState<TimelineFilter>({
    types: [],
    categories: [],
    severityRange: { min: 1, max: 10 },
    searchQuery: '',
  });

  const dateRange = useMemo(() => ({
    startDate: format(startOfMonth(currentMonth), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(currentMonth), 'yyyy-MM-dd'),
  }), [currentMonth]);

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const handleFilterChange = (newFilters: TimelineFilter) => {
    setFilters(newFilters);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.types.length > 0) count++;
    if (filters.categories.length > 0) count++;
    if (filters.severityRange.min > 1 || filters.severityRange.max < 10) count++;
    if (filters.searchQuery) count++;
    return count;
  }, [filters]);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-navy" />
              </button>
              <div className="p-2 bg-navy/10 rounded-xl">
                <Clock className="w-6 h-6 text-navy" />
              </div>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">
                  Health Timeline
                </h1>
                <p className="text-sm text-warm-gray">
                  View your health history over time
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center bg-cream rounded-lg p-1">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'timeline'
                      ? 'bg-white shadow-sm text-navy'
                      : 'text-warm-gray hover:text-navy'
                  }`}
                  title="Timeline View"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-white shadow-sm text-navy'
                      : 'text-warm-gray hover:text-navy'
                  }`}
                  title="Calendar View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-sage text-white'
                    : 'bg-cream text-navy hover:bg-cream-dark'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between py-3 border-t border-warm-gray/10">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousMonth}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-navy" />
              </button>
              <button
                onClick={handleNextMonth}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-navy" />
              </button>
              <span className="font-display font-semibold text-navy ml-2">
                {format(currentMonth, 'MMMM yyyy')}
              </span>
            </div>

            <button
              onClick={handleToday}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-sage
                       hover:bg-sage/10 rounded-lg transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Today
            </button>
          </div>
        </div>
      </header>

      {/* Filters Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white border-b border-warm-gray/10"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <TimelineFilters
              filters={filters}
              onChange={handleFilterChange}
            />
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {viewMode === 'timeline' ? (
          <HealthTimeline
            childId={childId || ''}
            dateRange={dateRange}
            filters={filters}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-warm-gray/10 p-8">
            <div className="text-center text-warm-gray">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Calendar View</p>
              <p className="text-sm mt-1">Coming soon - View health entries in calendar format</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HealthTimelinePage;

